import React, { useRef, useLayoutEffect, useState } from "react";
import doneStepIcon from "@surgia/ui/icons/steps/done.svg";
import notDoneStepsIcon from "@surgia/ui/icons/steps/notDone.svg";

interface Step {
  label: string;
}

interface GenericStepperProps {
  steps: Step[];
  currentStep: number;
}

// Size and color variables
const OUTER_SIZE = 32; // Outer (purple) ring diameter
const OUTER_BORDER = 3; // Purple border thickness
const GAP_SIZE = 25; // White gap diameter
const BLUE_SIZE = 24; // Blue circle diameter
const DOT_SIZE = 8; // White dot diameter
const CIRCLE_TOP = 4; // Top offset for the outer ring

const PURPLE = "#A084E8";
const BLUE = "#0033CC";
const NOT_SELECTED = "#414651";
const LIGHT_GRAY = "#E5E7EB";

// --- Section Components ---

const DoneStep = ({ isLastStep }: { isLastStep: boolean }) => (
  <div
    style={{
      width: OUTER_SIZE,
      height: OUTER_SIZE,
      position: "absolute",
      top: CIRCLE_TOP,
      left: "50%",
      transform: isLastStep ? "none" : "translateX(-50%)",
      zIndex: 1,
    }}
  >
    <img
      src={doneStepIcon}
      alt="Done"
      style={{
        width: OUTER_SIZE,
        height: OUTER_SIZE,
      }}
    />
  </div>
);

const NotDoneStep = ({ isLastStep }: { isLastStep: boolean }) => (
  <div
    style={{
      width: OUTER_SIZE,
      height: OUTER_SIZE,
      position: "absolute",
      top: CIRCLE_TOP,
      left: "50%",
      transform: isLastStep ? "none" : "translateX(-50%)",
      zIndex: 1,
    }}
  >
    <img
      src={notDoneStepsIcon}
      alt="Not Done"
      style={{
        width: OUTER_SIZE,
        height: OUTER_SIZE,
      }}
    />
  </div>
);

const InProgressStep = () => (
  <>
    <div
      style={{
        width: OUTER_SIZE,
        height: OUTER_SIZE,
        borderRadius: "50%",
        border: `${OUTER_BORDER}px solid ${PURPLE}`,
        position: "absolute",
        top: CIRCLE_TOP,
        left: "50%",
        transform: "translateX(-50%)",
        background: "#fff",
        zIndex: 1,
        boxSizing: "border-box",
      }}
    />
    <div
      style={{
        width: GAP_SIZE,
        height: GAP_SIZE,
        borderRadius: "50%",
        background: "#fff",
        position: "absolute",
        top: CIRCLE_TOP + (OUTER_SIZE - GAP_SIZE) / 2,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 2,
      }}
    />
    <div
      style={{
        width: BLUE_SIZE,
        height: BLUE_SIZE,
        borderRadius: "50%",
        background: BLUE,
        position: "absolute",
        top: CIRCLE_TOP + (OUTER_SIZE - BLUE_SIZE) / 2,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 3,
      }}
    />
    <div
      style={{
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: "50%",
        background: "#fff",
        position: "absolute",
        top: CIRCLE_TOP + (OUTER_SIZE - DOT_SIZE) / 2,
        left: "50%",
        transform: "translateX(-50%)",
        zIndex: 4,
      }}
    />
  </>
);

const ConnectorLine = ({ isCompleted }: { isCompleted: boolean }) => (
  <div
    className="absolute"
    style={{
      top: CIRCLE_TOP + OUTER_SIZE / 2 - 1, // center line vertically
      left: `calc(50% + ${OUTER_SIZE / 2}px)`,
      width: `calc(100% - ${OUTER_SIZE / 2}px)`,
      height: 2,
      background: isCompleted ? BLUE : LIGHT_GRAY,
      zIndex: 0,
    }}
  />
);

// --- Main Stepper ---

const GenericStepper: React.FC<GenericStepperProps> = ({
  steps,
  currentStep,
}) => {
  // Refs and state to measure label widths
  const labelRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const [labelWidths, setLabelWidths] = useState<number[]>([]);

  useLayoutEffect(() => {
    setLabelWidths(labelRefs.current.map((ref) => (ref ? ref.offsetWidth : 0)));
  }, [steps]);

  // Calculate minWidth for each step: max(labelWidth, OUTER_SIZE) + some padding
  const stepMinWidths = labelWidths.map(
    (w) => Math.max(w, OUTER_SIZE) + 80 // 16px padding
  );

  return (
    <div
      className="flex items-center justify-between w-full px-4 py-2"
      dir="ltr"
    >
      {steps.map((step, idx) => {
        const isCompleted = idx < currentStep;
        const isInProgress = idx === currentStep;
        const isNotStarted = idx > currentStep;
        const isLastStep = idx === steps.length - 1;

        return (
          <div
            key={step.label}
            className="flex-1 flex flex-col items-center relative"
            style={{
              minWidth: stepMinWidths[idx] || OUTER_SIZE + 16,
              flex: `0 1 ${stepMinWidths[idx] || OUTER_SIZE + 16}px`,
            }}
          >
            {/* Step Circle */}
            <div
              className="relative flex flex-col items-center"
              style={{ height: OUTER_SIZE + CIRCLE_TOP }}
            >
              {isCompleted && <DoneStep isLastStep={isLastStep} />}
              {isInProgress && <InProgressStep />}
              {isNotStarted && <NotDoneStep isLastStep={isLastStep} />}
            </div>

            {/* Step Label */}
            <span
              ref={(el) => {
                labelRefs.current[idx] = el;
              }}
              className="mt-2 text-center whitespace-nowrap"
              style={{
                color: isInProgress ? BLUE : NOT_SELECTED,
                fontSize: 14,
                fontWeight: isInProgress ? 700 : 400,
                lineHeight: "20px",
                display: "inline-block",
              }}
            >
              {step.label}
            </span>

            {/* Connector Line */}
            {idx < steps.length - 1 && (
              <ConnectorLine isCompleted={isCompleted} />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default GenericStepper;
